import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  GraduationCap,
  CheckCircle,
  FileEdit,
  XCircle,
  Search,
  Loader2,
  Clock,
  Euro,
} from 'lucide-react';
import {
  useFormationsAdmin,
  useFormationsStats,
  useFormationCategories,
  type FormationsFilters,
} from '@/hooks/useFormationsAdmin';

function StatCard({
  title,
  value,
  icon: Icon,
}: {
  title: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-gray-600">{title}</CardTitle>
        <Icon className="w-4 h-4 text-gray-500" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold text-gray-900">{value}</div>
      </CardContent>
    </Card>
  );
}

const statusBadge = {
  active: { label: 'Active', className: 'bg-green-100 text-green-800' },
  draft: { label: 'Brouillon', className: 'bg-yellow-100 text-yellow-800' },
  inactive: { label: 'Inactive', className: 'bg-gray-100 text-gray-800' },
};

export function FormationsManagement() {
  const [filters, setFilters] = useState<FormationsFilters>({
    status: 'all',
    category: undefined,
    lang: undefined,
    search: '',
  });

  const { data: formations, isLoading } = useFormationsAdmin(filters);
  const { data: stats } = useFormationsStats();
  const { data: categories } = useFormationCategories();

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Formations</h1>
        <p className="text-gray-600 mt-2">Gestion du catalogue de formations</p>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <StatCard title="Total formations" value={stats?.total || 0} icon={GraduationCap} />
        <StatCard title="Actives" value={stats?.active || 0} icon={CheckCircle} />
        <StatCard title="Brouillons" value={stats?.draft || 0} icon={FileEdit} />
        <StatCard title="Inactives" value={stats?.inactive || 0} icon={XCircle} />
      </div>

      {/* Filtres */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Filtres</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Rechercher par titre..."
              value={filters.search}
              onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
              className="pl-10"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Filtre statut */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Statut</label>
              <Tabs
                value={filters.status || 'all'}
                onValueChange={(v) =>
                  setFilters((prev) => ({ ...prev, status: v as FormationsFilters['status'] }))
                }
                className="w-full"
              >
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="all">Tous</TabsTrigger>
                  <TabsTrigger value="active">Actives</TabsTrigger>
                  <TabsTrigger value="draft">Brouillons</TabsTrigger>
                  <TabsTrigger value="inactive">Inactives</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {/* Filtre langue */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Langue</label>
              <Select
                value={filters.lang || 'all'}
                onValueChange={(v) =>
                  setFilters((prev) => ({ ...prev, lang: v === 'all' ? undefined : v }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Toutes les langues" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes</SelectItem>
                  <SelectItem value="fr">Français</SelectItem>
                  <SelectItem value="en">Anglais</SelectItem>
                  <SelectItem value="es">Espagnol</SelectItem>
                  <SelectItem value="de">Allemand</SelectItem>
                  <SelectItem value="it">Italien</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Filtre catégorie */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Catégorie</label>
              <Select
                value={filters.category || 'all'}
                onValueChange={(v) =>
                  setFilters((prev) => ({ ...prev, category: v === 'all' ? undefined : v }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Toutes les catégories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes</SelectItem>
                  {categories?.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table des formations */}
      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500">Chargement...</span>
            </div>
          ) : !formations || formations.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              Aucune formation trouvée
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Titre</TableHead>
                  <TableHead>Catégorie</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Durée</TableHead>
                  <TableHead>Prix HT</TableHead>
                  <TableHead>Participants</TableHead>
                  <TableHead>Langue</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {formations.map((formation) => {
                  const badge = statusBadge[formation.status] || statusBadge.draft;
                  return (
                    <TableRow key={formation.id}>
                      <TableCell className="font-medium max-w-xs truncate">
                        {formation.title}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{formation.category}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={badge.className}>{badge.label}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-gray-600">
                          <Clock className="w-3.5 h-3.5" />
                          <span>
                            {formation.duration_days}j ({formation.duration_hours}h)
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-gray-600">
                          <Euro className="w-3.5 h-3.5" />
                          <span>{formation.price_ht}€</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-600">
                        {formation.min_participants}–{formation.max_participants}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="uppercase text-xs">
                          {formation.lang || 'fr'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
